import { Box, Text } from '@dixa/lib-shared-habla';
import * as Popover from '@radix-ui/react-popover';
import { useCallback, useMemo, useState } from 'react';

import { presenceApi } from 'apis/presence';
import { PresenceOverlayPopover } from 'components/presence/components/PresenceOverlayPopover';
import { LegacyPresenceListSections } from 'components/presence/components/legacyPresenceListSections';
import { PresenceRowSelection } from 'components/presence/components/presenceListItem';
import { PresenceListSections } from 'components/presence/components/presenceListSections';
import { PresenceSectionWarning } from 'components/presence/components/presenceSectionWarning';
import { SidebarIconButton } from 'components/presence/components/sidebarIconButton';
import { UsePresenceMembersResult } from 'components/presence/hooks/usePresenceMembers';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCurrentUser } from 'utils/user';

import styles from './presenceList.module.scss';

type PresenceListProps = {
  /** From `usePresenceMembers`, owned by a parent that outlives this list. */
  presenceMembers: UsePresenceMembersResult;
  /** Shows the add-agent button in the "Team" header of the new design. */
  onAddAgent?: () => void;
};

export const PresenceList = ({
  presenceMembers,
  onAddAgent,
}: PresenceListProps): JSX.Element => {
  const { user } = useCurrentUser();
  const flags = useFeatureFlag([
    'custom-away-status',
    'legacy-presence-sidebar',
  ]);

  const { awayStatuses } = presenceApi.useReadAwayStatusesQuery(
    { isDeactivated: false },
    {
      skip: !flags.customAwayStatus,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        awayStatuses: data?.data.customAwayStatuses ?? [],
      }),
    },
  );

  const { members, teams, isLoading, isError } = presenceMembers;

  // One popover for the whole list, attached to the clicked row: only one can
  // be open at a time, and one per row is too costly with every agent listed.
  const [selected, setSelected] = useState<{
    rowKey: string;
    memberId: string;
  } | null>(null);
  const selectedMember = selected
    ? members.find(({ id }) => id === selected.memberId)
    : undefined;

  const handleRowClick = useCallback((rowKey: string, memberId: string) => {
    setSelected((current) =>
      current?.rowKey === rowKey ? null : { rowKey, memberId },
    );
  }, []);

  // Only closes if it's still the selected row, as the previous one also
  // unmounts its anchor when another row is clicked.
  const handleRowRemoved = useCallback((rowKey: string) => {
    setSelected((current) => (current?.rowKey === rowKey ? null : current));
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setSelected(null);
    }
  }, []);

  const selection: PresenceRowSelection = useMemo(
    () => ({
      rowKey: selected?.rowKey,
      onRowClick: handleRowClick,
      onRowRemoved: handleRowRemoved,
    }),
    [selected?.rowKey, handleRowClick, handleRowRemoved],
  );

  const isOffline = useAppSelector((state) => state.presence.meta.offline);
  const hasFailed = isError && members.length === 0;

  const renderSections = () => {
    if (hasFailed) {
      return <PresenceSectionWarning title="Presence loading failed.." />;
    }

    if (isOffline) {
      return <PresenceSectionWarning title="You are offline" />;
    }

    if (flags.legacyPresenceSidebar) {
      return (
        <LegacyPresenceListSections
          members={members}
          profile={user}
          awayStatuses={awayStatuses}
          isLoading={isLoading}
          selection={selection}
        />
      );
    }

    return (
      <PresenceListSections
        profile={user}
        awayStatuses={awayStatuses}
        members={members}
        teams={teams}
        isLoading={isLoading}
        isError={isError}
        isPopoverOpen={!!selected}
        selection={selection}
      />
    );
  };

  return (
    <Box padding="0 200">
      {!flags.legacyPresenceSidebar && (
        <div className={styles.teamHeader}>
          <Text.Heading variant="heading-3" as="h2" color="inherit">
            Team
          </Text.Heading>
          {onAddAgent && (
            <SidebarIconButton
              icon="user-plus"
              label="Add new agent"
              onClick={onAddAgent}
            />
          )}
        </div>
      )}
      <Popover.Root open={!!selectedMember} onOpenChange={handleOpenChange}>
        <ul className={styles.list}>{renderSections()}</ul>
        {selectedMember && (
          <PresenceOverlayPopover
            key={selectedMember.id}
            member={selectedMember}
            ownProfile={user}
          />
        )}
      </Popover.Root>
    </Box>
  );
};
